import { ParsedUrlQuery } from 'querystring'
import { parse as parseQueryString } from 'querystring'
import {
    AccessType,
    ALL_ACCESS_TYPES,
} from 'common/src/api/websiteFrontendVsWebsiteBackend/accountMaintenance/types'

/**
 * Link-driven purchase for courses that are hidden from the listings
 * (ReleaseStatus.hiddenButAvailable): `/?purchase=<courseName>` optionally
 * with `&accessTypes=PRACTICE_PROBLEMS` (repeatable). Logged out, the home
 * page opens sign-up with a purchase intent so the new account lands on
 * checkout; logged in, the dashboard forwards straight to checkout.
 */
export const PURCHASE_LINK_QUERY_PARAM = 'purchase'

export interface PurchaseLinkIntent {
    courseName: string
    accessTypes: AccessType[]
}

export const parsePurchaseLinkQuery = (
    query: ParsedUrlQuery
): PurchaseLinkIntent | null => {
    const rawCourseName = query[PURCHASE_LINK_QUERY_PARAM]
    const courseName = Array.isArray(rawCourseName)
        ? rawCourseName[0]
        : rawCourseName
    if (!courseName) {
        return null
    }

    const rawAccessTypes = query.accessTypes
    const requestedAccessTypes = (
        Array.isArray(rawAccessTypes)
            ? rawAccessTypes
            : rawAccessTypes
              ? [rawAccessTypes]
              : []
    ).filter((accessType): accessType is AccessType =>
        ALL_ACCESS_TYPES.includes(accessType as AccessType)
    )

    return {
        courseName,
        accessTypes: requestedAccessTypes.length
            ? requestedAccessTypes
            : ALL_ACCESS_TYPES,
    }
}

/**
 * Same as parsePurchaseLinkQuery but from `router.asPath`, which carries the
 * query string on the very first render of a statically rendered page,
 * before `router.isReady` flips and `router.query` is populated.
 */
export const parsePurchaseLinkFromAsPath = (
    asPath: string
): PurchaseLinkIntent | null => {
    const queryStart = asPath.indexOf('?')
    if (queryStart < 0) {
        return null
    }
    return parsePurchaseLinkQuery(
        parseQueryString(asPath.slice(queryStart + 1))
    )
}
